import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { PiArrowRight, PiChartPieSlice } from 'react-icons/pi';
import BudgetProgress from '../BudgetProgress';
import SensitiveMoney, { SensitiveMoneyGroup } from '../SensitiveMoney';
import { BUDGET_PROGRESS_STATE } from '../../domain/finance';
import { toYearMonthParam } from '../../utils/period';
import './styles.css';

const CATEGORY_PRIORITY = Object.freeze({
  [BUDGET_PROGRESS_STATE.EXCEEDED]: 3,
  [BUDGET_PROGRESS_STATE.REACHED]: 2,
  [BUDGET_PROGRESS_STATE.WARNING]: 1,
});

export const getRelevantCategories = (categories = []) => [...categories]
  .sort((left, right) => {
    const leftPriority = CATEGORY_PRIORITY[left.state] || 0;
    const rightPriority = CATEGORY_PRIORITY[right.state] || 0;
    if (rightPriority !== leftPriority) return rightPriority - leftPriority;
    return right.percentage - left.percentage
      || right.committedExpenseCents - left.committedExpenseCents
      || left.category.localeCompare(right.category, 'pt-BR');
  })
  .slice(0, 3);

const getStateLabel = (state) => ({
  [BUDGET_PROGRESS_STATE.NORMAL]: 'Dentro do orçamento',
  [BUDGET_PROGRESS_STATE.WARNING]: 'Próximo do limite',
  [BUDGET_PROGRESS_STATE.REACHED]: 'Limite atingido',
  [BUDGET_PROGRESS_STATE.EXCEEDED]: 'Orçamento ultrapassado',
}[state] || 'Orçamento mensal');

const BudgetCategoryRow = ({ category }) => {
  const exceeded = category.state === BUDGET_PROGRESS_STATE.EXCEEDED;

  return (
    <li className={`nf-budget-home__category is-${category.state}`}>
      <div>
        <strong>{category.category}</strong>
        <span>{category.percentage}% utilizado</span>
      </div>
      <SensitiveMoneyGroup className="nf-financial-value">
        <SensitiveMoney valueCents={category.remainingCents} absolute /> {exceeded ? 'acima' : 'disponíveis'}
      </SensitiveMoneyGroup>
    </li>
  );
};

BudgetCategoryRow.propTypes = {
  category: PropTypes.shape({
    category: PropTypes.string.isRequired,
    committedExpenseCents: PropTypes.number.isRequired,
    percentage: PropTypes.number.isRequired,
    remainingCents: PropTypes.number.isRequired,
  }).isRequired,
};

const BudgetSummaryCard = ({ budget = null, usage, period, loading = false, error = null }) => {
  const periodParam = toYearMonthParam(period);
  const planningHref = periodParam ? '/planejamento?period=' + periodParam : '/planejamento';
  if (loading) {
    return <section className="nf-budget-home nf-budget-home--loading" aria-label="Carregando orçamento" />;
  }

  if (error || !budget) {
    return (
      <section className="nf-budget-home nf-budget-home--empty">
        <div className="nf-budget-home__icon"><PiChartPieSlice aria-hidden="true" /></div>
        <div>
          <h2>Orçamento do mês</h2>
          <p>{error ? 'Não foi possível carregar o orçamento agora.' : 'Você ainda não definiu um orçamento para este mês.'}</p>
        </div>
        <Link to={planningHref}>
          {error ? 'Ver orçamento' : 'Criar orçamento'} <PiArrowRight aria-hidden="true" />
        </Link>
      </section>
    );
  }

  const monthly = usage.monthly;
  const remaining = monthly.remainingCents;
  const categories = getRelevantCategories(usage.categories);

  return (
    <section className={`nf-budget-home nf-budget-home--${monthly.state}`}>
      <div className="nf-budget-home__summary">
        <div className="nf-budget-home__icon"><PiChartPieSlice aria-hidden="true" /></div>
        <div className="nf-budget-home__content">
          <div className="nf-budget-home__heading">
            <div>
              <h2>Orçamento do mês</h2>
              {monthly.limitCents > 0 ? (
                <SensitiveMoneyGroup as="p" className="nf-budget-home__used">
                  <SensitiveMoney as="strong" className="nf-financial-value" valueCents={monthly.usedCents} />
                  <span>de <SensitiveMoney valueCents={monthly.limitCents} /> utilizados</span>
                </SensitiveMoneyGroup>
              ) : <strong className="nf-budget-home__undefined">Limite geral ainda não definido</strong>}
            </div>
            {monthly.limitCents > 0 && (
              <div className="nf-budget-home__state">
                <strong>{monthly.percentage}%</strong>
                <span>{getStateLabel(monthly.state)}</span>
              </div>
            )}
          </div>
          {monthly.limitCents > 0 && (
            <>
              <BudgetProgress percentage={monthly.percentage} state={monthly.state} label={`${monthly.percentage}% do orçamento utilizado`} />
              <SensitiveMoneyGroup as="p" className={`nf-budget-home__remaining${remaining < 0 ? ' is-exceeded' : ''}`}>
                <SensitiveMoney as="strong" className="nf-financial-value" valueCents={remaining} absolute />
                <span>{remaining < 0 ? 'acima do limite' : 'disponíveis'}</span>
              </SensitiveMoneyGroup>
            </>
          )}
        </div>
      </div>

      {categories.length > 0 && (
        <ul className="nf-budget-home__categories" aria-label="Categorias relevantes do orçamento">
          {categories.map((category) => <BudgetCategoryRow key={category.category} category={category} />)}
        </ul>
      )}

      <Link className="nf-budget-home__cta" to={planningHref}>
        Ver orçamento <PiArrowRight aria-hidden="true" />
      </Link>
    </section>
  );
};

BudgetSummaryCard.propTypes = {
  budget: PropTypes.shape({ monthlyLimitCents: PropTypes.number }),
  period: PropTypes.string.isRequired,
  usage: PropTypes.shape({
    monthly: PropTypes.shape({
      limitCents: PropTypes.number.isRequired,
      remainingCents: PropTypes.number.isRequired,
      usedCents: PropTypes.number.isRequired,
      percentage: PropTypes.number.isRequired,
      state: PropTypes.string.isRequired,
    }).isRequired,
    categories: PropTypes.arrayOf(PropTypes.shape({
      category: PropTypes.string.isRequired,
      committedExpenseCents: PropTypes.number.isRequired,
      percentage: PropTypes.number.isRequired,
      remainingCents: PropTypes.number.isRequired,
    })).isRequired,
  }).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.object,
};


export default BudgetSummaryCard;
